import TableForm from 'components/common/lists/TableForm';
import {
  TDeleteRegisteredRoundParams,
  TRegisteredChartInfo,
} from 'types/apis/professor';
import { TTableColumn, TTablePaging } from 'types/components/table';
import { ReactComponent as CloseIcon } from 'assets/icons/close_red.svg';
import { ReactComponent as PencilIcon } from 'assets/icons/pencil.svg';
import { ReactComponent as DisabledPencilIcon } from 'assets/icons/pencil_disabled.svg';
import { STYLES_VARIABLES } from 'constants/styleVariables';
import useModal from 'hooks/useModal';
import RegisterRoundModal from 'pages/modal/RegisterRoundModal';
import DeleteRound from 'pages/modal/DeleteRound';
import { useMutation, useQuery } from 'react-query';
import {
  deleteRegisteredRound,
  getCheckIsNeedPasswordForDelete,
} from 'apis/professor';
import { postPositionWithWord } from 'utils/postPosition';

type TProps = {
  activeTabIndex: number;
  data: TRegisteredChartInfo[];
  paging: TTablePaging;
  sortBy: string;
  sortDirection: string;
  onClickSort: (label: string) => void;
  refetchRound: () => void;
  isLoading: boolean;
};

const tableColumns: TTableColumn[] = [
  { name: 'no', label: '번호', width: '60px' },
  { name: 'courseName', label: '강의명', width: '180px' },
  { name: 'studClass', label: '분반', width: '60px' },
  {
    name: 'chartRoundAndChartName',
    label: '(회차) 회차명',
    width: '200px',
    onClick: () => {
      return;
    },
  },
  { name: 'classSize', label: '정원', width: '100px' },
  {
    name: 'firstSubmitEndDate',
    label: '1차 마감 일자',
    width: '150px',
    onClick: () => {
      return;
    },
  },
  {
    name: 'secondSubmitEndDate',
    label: '2차 마감 일자',
    width: '150px',
    onClick: () => {
      return;
    },
  },
  { name: '', label: '', width: '120px' },
];

const RegisteredRoundTable = ({
  activeTabIndex,
  data,
  paging,
  sortBy,
  sortDirection,
  onClickSort,
  refetchRound,
  isLoading,
}: TProps) => {
  const { openModal, closeModal } = useModal();

  const chartId = data[0]?.registeredChartId;
  const chartName = data[0]?.chartRoundAndChartName;

  const { data: getCheckIsNeedPasswordForDeleteData } = useQuery(
    'getCheckIsNeedPasswordForDelete',
    () => getCheckIsNeedPasswordForDelete(chartId as number),
    {
      enabled: !!chartId,
    }
  );

  const { mutate: deleteRegisteredRoundMutation } = useMutation(
    ({
      chartId,
      data,
    }: {
      chartId: number;
      data?: TDeleteRegisteredRoundParams;
    }) => deleteRegisteredRound(chartId, data),
    {
      onSuccess: () => {
        openModal({
          title: '차트가 삭제되었습니다.',
          text: `${postPositionWithWord({
            format: ['이', '가'],
            word: chartName || '',
          })} 삭제되었습니다.`,
          onSubmit: () => {
            closeModal();
            refetchRound();
          },
        });
      },
    }
  );

  const onClickEdit = (chartId: number) => {
    openModal({
      element: <RegisterRoundModal chartId={chartId} />,
    });
  };

  const onClickDelete = (chartId: number, chartName: string) => {
    // 연관된 학생의 실습 차트가 있을 경우 비밀번호 입력 후 삭제, 없을 경우 바로 삭제
    getCheckIsNeedPasswordForDeleteData?.data?.isNeedPassword
      ? openModal({
          element: (
            <DeleteRound
              chartId={chartId}
              chartName={chartName}
              refetchRound={refetchRound}
            />
          ),
        })
      : deleteRegisteredRoundMutation({
          chartId,
          data: {},
        });
  };

  const renderActions = (row: TRegisteredChartInfo, headerIndex?: number) => (
    <>
      {headerIndex === 7 && (
        <div className="flex" style={{ gap: '19px' }}>
          <button
            onClick={() => onClickEdit(row.registeredChartId)}
            disabled={activeTabIndex === 1}
          >
            {activeTabIndex === 0 ? (
              <PencilIcon width={32} height={32} />
            ) : (
              <DisabledPencilIcon width={32} height={32} />
            )}
          </button>
          <button
            onClick={() =>
              onClickDelete(row.registeredChartId, row.chartRoundAndChartName)
            }
          >
            <CloseIcon width={32} height={32} fill={STYLES_VARIABLES.GRAY_07} />
          </button>
        </div>
      )}
    </>
  );

  return (
    <>
      {/* 등록 회차 */}
      {activeTabIndex === 0 && (
        <div>
          <TableForm
            data={data}
            columns={tableColumns}
            paging={paging}
            sortBy={sortBy}
            sortDirection={sortDirection}
            onClickSort={onClickSort}
            renderActions={renderActions}
            emptyMessage="현재 등록된 회차가 없습니다."
            isLoading={isLoading}
          />
        </div>
      )}

      {/* 마감 회차 */}
      {activeTabIndex === 1 && (
        <div>
          <TableForm
            data={data}
            columns={tableColumns}
            paging={paging}
            sortBy={sortBy}
            sortDirection={sortDirection}
            onClickSort={onClickSort}
            renderActions={renderActions}
            emptyMessage="등록 마감된 회차가 없습니다."
            isLoading={isLoading}
          />
        </div>
      )}
    </>
  );
};

export default RegisteredRoundTable;
